import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { login as apiLogin, logout as apiLogout, checkAuth } from '../api';

// Create context for authentication state
const AuthContext = createContext();

/**
 * Authentication Provider Component
 * Manages global authentication state
 *
 * @param {Object} props Component props
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        setLoading(true);
        setAuthError(null);
        const authenticated = await checkAuth();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Auth check error:', error);
        setIsAuthenticated(false);
        setAuthError(error.message || 'Authentication check failed');
      } finally {
        setLoading(false);
        setInitialCheckDone(true);
      }
    };

    verifyAuth();
  }, []);

  // Periodically refresh auth status
  useEffect(() => {
    // Only set up refresh timer if we're authenticated
    if (!isAuthenticated) return;

    // Refresh auth check every 5 minutes to keep session alive
    const refreshInterval = setInterval(async () => {
      try {
        const authenticated = await checkAuth();

        // Only update state if authentication changed
        if (authenticated !== isAuthenticated) {
          setIsAuthenticated(authenticated);
        }
      } catch (error) {
        console.error('Auth refresh error:', error);
        // Only log the error but don't change auth state on refresh errors
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  // Login function
  const login = useCallback(async (password) => {
    try {
      setLoading(true);
      setAuthError(null);
      const response = await apiLogin(password);

      if (response && response.success) {
        setIsAuthenticated(true);
        return true;
      }

      setAuthError(response.message || 'Login failed');
      return false;
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await apiLogout();
      setIsAuthenticated(false);
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout API fails, we still consider user logged out on client
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Context value
  const value = {
    isAuthenticated,
    loading,
    initialCheckDone,
    authError,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook for using authentication context
 *
 * @returns {Object} Authentication state and functions
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
